import React, {useMemo, useState, useEffect, useCallback, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {Text, StatusBadge, TextInput, SelectInput, Button} from '@/ui-kits';
import {DataTable} from '@/components';
import {DataTableColumn, SelectInputitem, GetSpecialistOnboardingListRequestPayload, SpecialistOnboardingItem} from '@/interface';
import {SpecialistAdminOnboardingStatusLabels, SpecialistAdminOnboardingStatusBadgeVariants} from '@/constants/defaults';
import {SpecialistOnboardingStatus} from '@/constants/enums';
import {formatName, formatDate} from '@/utils/common_functions';
import {Routes} from '@/navigation/Routes';
import {useDispatch, useSelector} from 'react-redux';
import {
  specialistOnboardingListSelector,
  specialistOnboardingCurrentPageSelector,
  specialistOnboardingTotalPagesSelector,
  specialistOnboardingTotalResultsSelector,
  specialistAdminDashboardStatus,
  specialistAdminDashboardFailureSelector,
  hospitalListSelector,
} from '@/services/redux/selectors';
import {appCountries} from '@/services/redux/selectors/appSettingSelector';
import {getSpecialistOnboardingListRequest, resetSpecialistAdminMessage} from '@/services/redux/slices';
import {ErrorCodes, getErrorMessage} from '@/utils/getMessages';
import {useToast} from '@/hooks';

const statusOptions: SelectInputitem[] = [
  {id: SpecialistOnboardingStatus.Incomplete, label: 'Incomplete'},
  {id: SpecialistOnboardingStatus.UnderReview, label: 'Under Review'},
  {id: SpecialistOnboardingStatus.ChangesRequested, label: 'Changes Required'},
  {id: SpecialistOnboardingStatus.Approved, label: 'Approved'},
  {id: SpecialistOnboardingStatus.ProfileEdited, label: 'Profile Edited'},
];

interface SpecialistOnboardingData {
  id: string;
  name: string;
  specialization: string;
  hospital_name: string | null;
  fee: string | null;
  fee_currency: number | null;
  currency_symbol: string;
  signup_date: string;
  onboarding_status: SpecialistOnboardingStatus;
  visibility: 'Active' | 'Inactive';
  has_details: boolean;
}

export const SpecialistAdminOnboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {showToast} = useToast();

  // ==========================
  // Redux Selectors
  // ==========================
  const specialists = useSelector(specialistOnboardingListSelector);
  const currentPage = useSelector(specialistOnboardingCurrentPageSelector);
  const totalPages = useSelector(specialistOnboardingTotalPagesSelector);
  const totalResults = useSelector(specialistOnboardingTotalResultsSelector);
  const isLoading = useSelector(specialistAdminDashboardStatus);
  const failure = useSelector(specialistAdminDashboardFailureSelector) as ErrorCodes;
  const countries = useSelector(appCountries);
  const hospitals = useSelector(hospitalListSelector);
  // ==========================
  // States
  // ==========================
  const [search, setSearch] = useState<string>('');
  const [hospitalFilter, setHospitalFilter] = useState<SelectInputitem | null>(null);
  const [statusFilter, setStatusFilter] = useState<SelectInputitem | null>(null);
  const [page, setPage] = useState(1);
  const prevFiltersRef = useRef({search, statusFilter});

  // ==========================
  // Helper Functions
  // ==========================

  // Check if any filters are currently active
  const isFilterActive = Boolean(search || statusFilter || hospitalFilter);

  // Reset all filters and return to first page
  const resetFilter = () => {
    setSearch('');
    setStatusFilter(null);
    setHospitalFilter(null);
    setPage(1);
  };

  // Transform API data to component format for DataTable
  const transformedData = useMemo(() => {
    return specialists.map((specialist: SpecialistOnboardingItem) => {
      // Find currency symbol from countries list based on fee_currency ID
      const currencySymbol =
        specialist.fee_currency && countries.length > 0 ? countries.find(c => c.id === specialist.fee_currency)?.currency_symbol || '' : '';

      return {
        id: `${specialist.id}`,
        name: specialist.name,
        specialization: specialist.specialization.label,
        hospital_name: specialist.hospital_name || null,
        fee: specialist.fee ? `${specialist.fee.toLocaleString()}` : null,
        fee_currency: specialist.fee_currency || null,
        currency_symbol: currencySymbol,
        signup_date: specialist.signup_date,
        onboarding_status: specialist.profile_status.id as SpecialistOnboardingStatus,
        visibility: specialist.is_visible ? 'Active' : 'Inactive',
        has_details: specialist.profile_status.id !== SpecialistOnboardingStatus.Incomplete,
      };
    }) as SpecialistOnboardingData[];
  }, [specialists, countries]);

  // Define DataTable columns configuration
  const columns = useMemo(
    (): DataTableColumn<SpecialistOnboardingData>[] => [
      {
        name: 'id',
        align: 'left',
        title: <Text>SPECIALIST ID</Text>,
        render: row => <Text>{row.id}</Text>,
        width: {minWidth: '150px', maxWidth: '180px'},
      },
      {
        name: 'name',
        align: 'left',
        title: <Text>NAME</Text>,
        render: row => <Text className="break-words text-wrap">{formatName(row.name)}</Text>,
        width: {minWidth: '250px', maxWidth: '300px'},
      },
      {
        name: 'specialization',
        align: 'left',
        title: <Text>SPECIALIZATION</Text>,
        render: row => <Text className="break-words text-wrap">{row.specialization || '--'}</Text>,
        width: {minWidth: '300px', maxWidth: '350px'},
      },
      {
        name: 'hospital_name',
        align: 'left',
        title: <Text>HOSPITAL NAME</Text>,
        render: row => <Text className="break-words text-wrap">{row.hospital_name || '--'}</Text>,
        width: {minWidth: '250px', maxWidth: '300px'},
      },
      {
        name: 'fee',
        align: 'center',
        title: <Text>FEE</Text>,
        render: row => <Text>{row.fee ? (row.currency_symbol ? `${row.currency_symbol} ${row.fee}` : row.fee) : '--'}</Text>,
        width: {minWidth: '160px', maxWidth: '170px'},
      },
      {
        name: 'signup_date',
        align: 'center',
        title: <Text>SIGNUP DATE</Text>,
        render: row => <Text>{row.signup_date ? formatDate(row.signup_date, 'dd-MMM-yyyy, HH:mm:ss') : '--'}</Text>,
        width: {minWidth: '220px', maxWidth: '250px'},
      },
      {
        name: 'onboarding_status',
        align: 'center',
        title: <Text>ONBOARDING STATUS</Text>,
        render: row => (
          <StatusBadge
            variant="secondary"
            className="py-1"
            status={
              SpecialistAdminOnboardingStatusBadgeVariants[row.onboarding_status] ||
              SpecialistAdminOnboardingStatusBadgeVariants[SpecialistOnboardingStatus.Incomplete]
            }
            children={
              SpecialistAdminOnboardingStatusLabels[row.onboarding_status] || SpecialistAdminOnboardingStatusLabels[SpecialistOnboardingStatus.Incomplete]
            }
          />
        ),
        width: {minWidth: '250px', maxWidth: '300px'},
      },
      {
        name: 'visibility',
        align: 'center',
        title: <Text>VISIBILITY</Text>,
        render: row => <Text className={row.visibility === 'Active' ? 'text-ui_btn_green !font-FigtreeSemiBold' : ''}>{row.visibility}</Text>,
        width: {minWidth: '130px', maxWidth: '150px'},
      },
      {
        name: 'details',
        align: 'center',
        title: <Text>DETAILS</Text>,
        render: row =>
          row.has_details ? (
            <button
              onClick={() => navigate(Routes.SPECIALIST_ADMIN_PROFILE_DETAIL.replace(':id', row.id.replace('SP-', '')))}
              className="text-ui_blue hover:underline cursor-pointer">
              <Text className="text-ui_blue_background">View</Text>
            </button>
          ) : (
            <Text>--</Text>
          ),
        width: {minWidth: '120px', maxWidth: '150px'},
      },
    ],
    [],
  );

  // ==========================
  // useEffect Hooks
  // ==========================

  // Reset page to 1 when search or status filter changes
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged = prevFilters.search !== search || prevFilters.statusFilter?.id !== statusFilter?.id;
    if (filtersChanged) {
      setPage(1);
      prevFiltersRef.current = {search, statusFilter};
    }
  }, [search, statusFilter]);

  // Fetch onboarding list with debounce when filters or page changes
  useEffect(() => {
    const timeout = setTimeout(() => {
      const payload: GetSpecialistOnboardingListRequestPayload = {page};
      if (search) payload.search_text = search;
      if (statusFilter) payload.status = statusFilter.id as number;
      if (hospitalFilter) payload.hospital = hospitalFilter.id as number;
      dispatch(getSpecialistOnboardingListRequest(payload));
    }, 500);
    return () => clearTimeout(timeout);
  }, [page, search, statusFilter, hospitalFilter, dispatch]);

  // Handle error messages from API calls
  useEffect(() => {
    if (failure && !['E-10044', 'E-10054'].includes(failure)) {
      showToast(getErrorMessage(failure), 'failure');
      dispatch(resetSpecialistAdminMessage());
    }
  }, [failure, showToast, dispatch]);

  return (
    <div className="p-2 sm:py-4 md:p-6 flex flex-col grow gap-4">
      <div className="flex flex-col gap-2">
        <Text variant="heading" className="text-lg text-ui_blue !font-FigtreeSemiBold">
          All Specialists
        </Text>
        <Text variant="body" className="text-ui_input_border_dark !text-lg !font-FigtreeRegular">
          Manage specialist onboarding and profile approvals
        </Text>
      </div>

      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={search}
          onChangeText={setSearch}
          leftIcon="search_blue"
          className="sm:max-w-[320px] w-full col-span-full"
          wrapperClassName="px-2 border"
          placeholder="Search by ID, name, specialization"
        />
        <SelectInput
          wrapperClassName="border"
          name="status"
          className="sm:max-w-[150px] w-full"
          itemTextClassName="text-wrap !text-sm"
          placeholder="Status"
          options={statusOptions}
          value={statusFilter?.id || ''}
          onChange={setStatusFilter}
        />
        <SelectInput
          wrapperClassName="border"
          name="hospital"
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
          placeholder="Hospital"
          options={hospitals}
          value={hospitalFilter?.id || ''}
          onChange={setHospitalFilter}
        />
        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={transformedData}
        totalPages={totalPages}
        currentPage={currentPage || page}
        totalResult={totalResults}
        onPageChange={setPage}
        pageSize={10}
        loading={isLoading}
      />
    </div>
  );
};
